import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { Skeleton } from "@/components/ui/Skeleton";
import { BodyText } from "@/components/ui/Text";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { formatDateTime } from "@/lib/time";
import { getInitials } from "@/lib/utils";
import { safeRetrieveName } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useEncounter } from "../../../../network/useEncounter";
import type { ChatMessage } from "../../types/chatMessageTypes";
import { UserAvatar } from "../UserAvatar";
import { MessageBubble } from "./MessageBubble";
interface EncounterStartMessageProps {
  message: ChatMessage;
}

export const EncounterStartMessage = ({
  message,
}: EncounterStartMessageProps) => {
  const [showSourceRfe, setShowSourceRfe] = useState(false);
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const {
    data: encounter,
    isError: encounterError,
    refetch: refetchEncounter,
  } = useEncounter(message.fhirEncounterId, patientId);
  const {
    data: patient,
    isError: patientError,
    refetch: refetchPatient,
  } = useGetPatient(patientId);

  if (encounterError || patientError) {
    return (
      <Callout
        className="cursor-pointer"
        onClick={() => {
          refetchEncounter();
          refetchPatient();
        }}
        variant="danger"
        title="Error loading encounter or patient."
        description="Please click here to try again."
      />
    );
  }

  if (!encounter || !patient) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="w-1/4 h-8 rounded-lg self-center" />
        <Skeleton className="w-full h-16 rounded-lg" />
      </div>
    );
  }

  const patientName = safeRetrieveName(patient);
  const patientInitials = getInitials(patientName);

  const finalRfe = encounter.rfe;
  const sourceRfe = encounter.rfe_source_text;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <BodyText size="sm" className="self-center">
          Visit started
        </BodyText>
        <BodyText size="sm" textColor="tertiary" className="self-center">
          {formatDateTime(message.created)}
        </BodyText>
      </div>
      {/* HACK: not sending in a user from userMap because one
      is not guaranteed to exist if no messages have been sent yet */}
      <UserAvatar
        fallbackName={patientName}
        fallbackInitials={patientInitials}
        isCustomer={true}
      />
      <MessageBubble
        timestamp={message.created}
        variant="default"
        footer={
          sourceRfe && sourceRfe !== finalRfe ? (
            <Button
              className="self-start"
              variant="link"
              size="xs"
              onClick={() => setShowSourceRfe((prev) => !prev)}
            >
              {showSourceRfe ? "Show translation" : "Show original"}
            </Button>
          ) : null
        }
      >
        <BodyText size="sm">{showSourceRfe ? sourceRfe : finalRfe}</BodyText>
      </MessageBubble>
    </div>
  );
};
